import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import ListSubheader from "@mui/material/ListSubheader";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useState } from "react";
import { Link as RouterLink, Outlet } from "react-router-dom";
import {
  MODE_SHELL_EXPANDED_QUERY,
  BrandMark,
  ModeShell,
  ShellAvatar,
  ShellMenu,
} from "@bw/shared/components";
import { useAuth } from "@bw/shared/hooks";

import { allowAll, platformNavigation } from "../navigation/modes";

type PlatformEnvironment = "Local" | "Preview" | "Staging" | "Production";

/** Which deployment this admin session is acting on, from the page's host. */
export function platformEnvironment(hostname: string): PlatformEnvironment {
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]") {
    return "Local";
  }
  if (/(^|[.-])staging([.-]|$)/.test(hostname)) return "Staging";
  if (/(^|[.-])(preview|pr-\d+)([.-]|$)/.test(hostname)) return "Preview";
  return "Production";
}

function Brand() {
  // Phone ("Shell states"): the mode pill leads the bar; the brand is desktop-only.
  const expanded = useMediaQuery(MODE_SHELL_EXPANDED_QUERY);
  if (!expanded) return null;
  return (
    <ButtonBase
      component={RouterLink}
      to="/"
      aria-label="Badminton Warriors platform home"
      sx={{ display: "flex", alignItems: "center", gap: 1, borderRadius: 1, flexShrink: 0 }}
    >
      <BrandMark aria-hidden="true" size="bar" />
      <Typography
        component="span"
        variant="body2"
        noWrap
        sx={{ fontWeight: 700, display: { xs: "none", sm: "inline" } }}
      >
        Platform admin
      </Typography>
    </ButtonBase>
  );
}

const ENVIRONMENT_SHORT: Record<PlatformEnvironment, string> = {
  Local: "LOCAL",
  Preview: "PREVIEW",
  Staging: "STAGING",
  Production: "PROD",
};

function EnvironmentChip() {
  const environment = platformEnvironment(window.location.hostname);
  const production = environment === "Production";
  return (
    <Box
      component="span"
      role="img"
      aria-label={`Environment: ${environment}`}
      sx={{
        display: { xs: "none", sm: "inline-flex" },
        alignItems: "center",
        height: 20,
        px: 0.75,
        borderRadius: "4px",
        border: 1,
        borderColor: production ? "warning.main" : "divider",
        color: production ? "warning.main" : "text.secondary",
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.04em",
        flexShrink: 0,
      }}
    >
      {ENVIRONMENT_SHORT[environment]}
    </Box>
  );
}

function initials(label: string): string {
  const words = label.replace(/@.*/, "").trim().split(/[\s._-]+/).filter(Boolean);
  const letters = words.length > 1 ? `${words[0]![0]}${words[1]![0]}` : label.slice(0, 2);
  return letters.toUpperCase();
}

function AccountMenu() {
  const { signOut, user } = useAuth();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const userName = user?.displayName?.trim() || user?.email?.trim() || "Admin";
  const open = Boolean(anchorEl);

  return (
    <>
      <ButtonBase
        type="button"
        aria-label={`Account menu for ${userName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ borderRadius: "50%", flexShrink: 0, ml: 0.5 }}
      >
        <ShellAvatar tone="emerald" src={user?.photoURL ?? undefined} alt="">
          {initials(userName)}
        </ShellAvatar>
      </ButtonBase>
      <ShellMenu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
        <ListSubheader
          component="li"
          disableSticky
          sx={{ bgcolor: "transparent", px: 1, pt: 1, pb: 1.25, lineHeight: 1.25 }}
        >
          <Typography noWrap sx={{ fontSize: 13, fontWeight: 700, color: "text.primary" }}>
            {userName}
          </Typography>
          {user?.email ? (
            <Typography color="text.secondary" noWrap component="p" sx={{ fontSize: 12 }}>
              {user.email}
            </Typography>
          ) : null}
        </ListSubheader>
        <Divider component="li" />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            void signOut();
          }}
        >
          <ListItemIcon>
            <LogoutOutlinedIcon aria-hidden="true" />
          </ListItemIcon>
          <ListItemText>Sign out</ListItemText>
        </MenuItem>
      </ShellMenu>
    </>
  );
}

export function PlatformLayout() {
  const { user } = useAuth();

  return (
    <ModeShell
      definition={platformNavigation}
      allow={allowAll}
      ready
      storageKey={`platform:${user?.uid ?? "anonymous"}`}
      tabVariant="pill"
      showPageIcons={false}
      sectionVariant="segmented"
      appearance="platform"
      phoneSwitcher="dropdown"
      searchWidth={380}
      brand={<Brand />}
      trailing={
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <EnvironmentChip />
          <AccountMenu />
        </Box>
      }
      mainSx={{
        display: "block",
        flex: 1,
        width: "100%",
        maxWidth: 1600,
        mx: "auto",
        px: { xs: 2, sm: 3, lg: 4 },
        py: { xs: 2, sm: 3, lg: 4 },
      }}
    >
      <Outlet />
    </ModeShell>
  );
}
